import { createContext, createElement, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { syncPositionProjects } from '../lib/syncPositionProjects'

const INTERVAL_MS = 15 * 60 * 1000 // 15 minutes
const SCHWAB_REFRESH_TOKEN_DAYS = 7
export const SCHWAB_SYNC_COMPLETE_EVENT = 'erevis:schwab-sync-complete'

function calcTokenWarning(createdAt) {
  if (!createdAt) return null
  const authDate = new Date(createdAt)
  const now = new Date()
  const daysElapsed = (now - authDate) / (1000 * 60 * 60 * 24)
  const daysRemaining = Math.max(0, SCHWAB_REFRESH_TOKEN_DAYS - daysElapsed)

  if (daysRemaining > 2) return null // days 1-5: no warning
  if (daysRemaining > 0) return { daysRemaining: Math.ceil(daysRemaining), level: 'warning' } // day 6
  return { daysRemaining: 0, level: 'expired' } // day 7+
}

export function useAutoSync() {
  const { user } = useAuth()
  const [lastSynced, setLastSynced] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const [tokenWarning, setTokenWarning] = useState(null)
  const [syncError, setSyncError] = useState(null)
  const intervalRef = useRef(null)
  const tokenReadVersion = useRef(0)

  // OAuth completion must update the warning even while a full sync is busy.
  // Read only saved metadata; do not rotate tokens or start another broker sync.
  const refreshTokenWarning = useCallback(async (currentUser = user) => {
    if (!currentUser?.id) return null
    const version = ++tokenReadVersion.current
    try {
      const { data: tokenRow, error } = await supabase
        .from('broker_tokens')
        .select('broker, created_at')
        .eq('user_id', currentUser.id)
        .eq('broker', 'schwab')
        .maybeSingle()

      if (error || version !== tokenReadVersion.current) return null
      const warning = calcTokenWarning(tokenRow?.created_at)
      setTokenWarning(warning)
      if (!warning) setSyncError(null)
      return { tokenRow, version }
    } catch {
      // An unavailable read is not proof that authorization was renewed.
      return null
    }
  }, [user?.id])

  const runSync = async (currentUser) => {
    if (!currentUser) return

    const tokenStatus = await refreshTokenWarning(currentUser)
    if (!tokenStatus?.tokenRow) return

    setSyncing(true)
    setSyncError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      // EF-123: removed pre-refresh call to /api/schwab/auth?action=refresh.
      // sync.js, orders.js, and oms/auth.js each have their own
      // getValidAccessToken that refreshes on-demand. The separate
      // pre-refresh created a race: if the user sent orders the same
      // moment auto-sync fired, both reads of broker_tokens returned the
      // same refresh_token; Schwab processed one, rotated the token, and
      // the other got "invalid JWT … token is expired". Removing this
      // eliminates the race. Genuine 7-day expiry is detected below by
      // the sync call itself returning a refresh-failed error.

      // Sync all data (sync.js also auto-refreshes if needed)
      const res = await fetch('/api/schwab/sync', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })

      if (res.ok) {
        const syncedAt = new Date()
        setLastSynced(syncedAt)

        // Capture a daily portfolio snapshot after each successful sync
        await fetch('/api/schwab/snapshot', {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        })

        // An order read is availability evidence only. Even a complete empty
        // list cannot turn a submitted setup back into an unsent Idea.
        let ordersAvailable = false
        try {
          const ordersRes = await fetch('/api/schwab/orders', {
            headers: { Authorization: `Bearer ${session.access_token}` },
          })
          const payload = ordersRes.ok ? await ordersRes.json() : null
          ordersAvailable = !!(payload?.success === true
            && payload.ordersComplete === true && Array.isArray(payload.orders))
        } catch {
          // Preserve all setup states on transport or payload failure.
        }

        await syncPositionProjects(currentUser.id)

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(SCHWAB_SYNC_COMPLETE_EVENT, {
            detail: {
              syncedAt: syncedAt.toISOString(),
              ordersAvailable,
            },
          }))
        }
      } else {
        const err = await res.json().catch(() => ({}))
        if (tokenStatus.version === tokenReadVersion.current
          && ((err.error || '').includes('refresh failed') || (err.error || '').includes('Token'))) {
          setSyncError('token_expired')
          setTokenWarning({ daysRemaining: 0, level: 'expired' })
        }
      }
    } catch {
      // Silent background sync — errors are not surfaced to the user
    } finally {
      setSyncing(false)
    }
  }

  const triggerSync = useCallback(() => {
    if (user && !syncing) runSync(user)
  }, [user?.id, syncing])

  useEffect(() => {
    if (!user) return

    runSync(user)

    intervalRef.current = setInterval(() => runSync(user), INTERVAL_MS)

    return () => {
      // Ignore reads and sync failures from a previous owner or unmounted hook.
      tokenReadVersion.current += 1
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [user?.id])

  return { lastSynced, syncing, tokenWarning, syncError, triggerSync, refreshTokenWarning }
}

// Context so useAutoSync runs once at the top of the tree while consumers
// (navbar status indicators) subscribe without forcing Layout to re-render
// on every sync state change. Layout does not consume this context —
// only sub-components do.
const AutoSyncContext = createContext(null)

export function AutoSyncProvider({ children }) {
  const value = useAutoSync()
  const memoized = useMemo(
    () => value,
    [value.lastSynced, value.syncing, value.tokenWarning, value.syncError, value.triggerSync, value.refreshTokenWarning]
  )
  return createElement(AutoSyncContext.Provider, { value: memoized }, children)
}

export function useAutoSyncContext() {
  const ctx = useContext(AutoSyncContext)
  if (!ctx) {
    throw new Error('useAutoSyncContext must be used inside <AutoSyncProvider>')
  }
  return ctx
}
